import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useI18n } from '@/i18n/LanguageContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import MediaUploader from '@/components/MediaUploader';
import { uploadCarImage } from '@/lib/uploadCarImage';
import { ChevronDown, X, FileText, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthProvider';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';

const EditCarPage = () => {
  const { carId } = useParams<{ carId: string }>();
  const { cars, updateCar } = useCar();
  const { settings } = useSettings();
  const { t } = useI18n();

  const formSchema = React.useMemo(() => z.object({
    brand: z.string().min(1, { message: t('cars.edit.validation.brandRequired') || 'Brand is required' }),
    model: z.string().min(1, { message: t('cars.edit.validation.modelRequired') || 'Model is required' }),
    year: z.string().regex(/^[0-9]{4}$/, { message: t('cars.edit.validation.yearInvalid') || 'Year must be a 4-digit number' }),
    nickname: z.string().optional(),
    licensePlate: z.string().min(1, { message: t('cars.edit.validation.licensePlateRequired') || 'License plate is required' }),
    vin: z.string().optional(),
    mileage: z.string().optional(),
    transmission: z.enum(['manual', 'automatic'], { required_error: t('cars.edit.validation.transmissionRequired') || 'Transmission is required', invalid_type_error: t('cars.edit.validation.transmissionRequired') || 'Transmission is required' }),
    fuelType: z.enum(['gasoline', 'diesel', 'electric', 'hybrid', 'lpg', 'cng'], { required_error: t('cars.edit.validation.fuelTypeRequired') || 'Fuel type is required', invalid_type_error: t('cars.edit.validation.fuelTypeRequired') || 'Fuel type is required' }),
    wiperDriversSideSize: z.string().optional(),
    wiperPassangerSideSize: z.string().optional(),
    wiperRearSize: z.string().optional(),
    wiperLastReplacement: z.string().optional(),
    lastServiceDate: z.string().optional(),
    winterTiresBrand: z.string().optional(),
    winterTiresSize: z.string().optional(),
    summerTiresBrand: z.string().optional(),
    summerTiresSize: z.string().optional(),
    tirePressure: z.string().optional(),
    octaPolicyNumber: z.string().optional(),
    octaExpiry: z.string().optional(),
    octaProvider: z.string().optional(),
    octaClaimsHotline: z.string().optional(),
    kaskoPolicyNumber: z.string().optional(),
    kaskoExpiry: z.string().optional(),
    kaskoProvider: z.string().optional(),
    kaskoClaimsHotline: z.string().optional(),
  }), [t]);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const currentCar = cars.find(car => car.id === carId);
  
  // Move all hooks to the top before any conditional returns
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(currentCar?.image_url || '');
  const [imageLoading, setImageLoading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // 🆕
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const octaFileInputRef = React.useRef<HTMLInputElement>(null);
  const kaskoFileInputRef = React.useRef<HTMLInputElement>(null);
  const [latestOdometerReading, setLatestOdometerReading] = useState<number | null>(null);

  // OCTA / KASKO documents (up to 5 each: images or PDFs)
  const [octaMediaPaths, setOctaMediaPaths] = useState<string[]>([]);
  const [octaPendingFiles, setOctaPendingFiles] = useState<File[]>([]);
  const [kaskoMediaPaths, setKaskoMediaPaths] = useState<string[]>([]);
  const [kaskoPendingFiles, setKaskoPendingFiles] = useState<File[]>([]);
  useEffect(() => {
    setOctaMediaPaths(currentCar?.octaMediaPaths ?? []);
    setKaskoMediaPaths(currentCar?.kaskoMediaPaths ?? []);
  }, [currentCar?.id, currentCar?.octaMediaPaths, currentCar?.kaskoMediaPaths]);
  
  const [activeAccordion, setActiveAccordion] = useState<null | 'basic' | 'tires' | 'wipers' | 'octa' | 'kasko'>('basic');
  const location = useLocation();

  // Open specific accordion based on query param ?section=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section') as 'basic' | 'tires' | 'wipers' | 'octa' | 'kasko' | null;
    if (section) setActiveAccordion(section);
  }, [location.search]);

  // Scroll into view when a specific accordion is active
  const tiresRef = React.useRef<HTMLDivElement>(null);
  const wipersRef = React.useRef<HTMLDivElement>(null);
  const octaRef = React.useRef<HTMLDivElement>(null);
  const kaskoRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section') as 'tires' | 'wipers' | 'octa' | 'kasko' | null;
    const map: Record<string, React.RefObject<HTMLDivElement>> = {
      tires: tiresRef,
      wipers: wipersRef,
      octa: octaRef,
      kasko: kaskoRef,
    };
    const target = section ? map[section] : null;
    if (target?.current) {
      // slight delay to ensure accordion content is rendered
      setTimeout(() => {
        target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [activeAccordion, location.search]);

  // Mask helpers for tire size inputs: ___ / __ R__ (7 digits total)
  const formatTireSizeInput = (raw: string) => {
    const digits = (raw || '').replace(/\D/g, '').slice(0, 7);
    const parts = [digits.slice(0,3), digits.slice(3,5), digits.slice(5,7)].filter(() => true);
    let out = '';
    if (parts[0]) out += parts[0];
    if (digits.length > 0) out = out.padEnd(3, '_');
    if (digits.length > 0) out += ' / ';
    if (parts[1]) out += parts[1];
    if (digits.length > 3) out = out.padEnd(3 + 3 + 2, '_');
    if (digits.length > 3) out += ' R';
    if (parts[2]) out += parts[2];
    return out;
  };

  const computeCaretPos = (masked: string) => {
    // place caret after last digit, skipping separators; if none, at start
    for (let i = masked.length - 1; i >= 0; i--) {
      if (/\d/.test(masked[i])) {
        return i + 1;
      }
    }
    // if no digits, position before first underscore
    const firstUnderscore = masked.indexOf('_');
    return firstUnderscore >= 0 ? firstUnderscore : masked.length;
  };
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      brand: '',
      model: '',
      year: '',
      nickname: '',
      licensePlate: '',
      vin: '',
      mileage: '',
      transmission: undefined,
      fuelType: undefined,
      // Wipers
      wiperDriversSideSize: '',
      wiperPassangerSideSize: '',
      wiperRearSize: '',
      wiperLastReplacement: '',
      lastServiceDate: '',
      winterTiresBrand: '',
      winterTiresSize: '',
      summerTiresBrand: '',
      summerTiresSize: '',
      tirePressure: '',
      octaPolicyNumber: '',
      octaExpiry: '',
      octaProvider: '',
      octaClaimsHotline: '',
      kaskoPolicyNumber: '',
      kaskoExpiry: '',
      kaskoProvider: '',
      kaskoClaimsHotline: '',
    }
  });
  
  const { user, loading } = useAuth();

  // Fetch latest odometer reading from odometer_readings table
  useEffect(() => {
    if (!carId) return;
    
    const fetchLatestOdometer = async () => {
      try {
        // Fetch top readings to filter out obvious outliers
        const { data, error } = await supabase
          .from('odometer_readings')
          .select('reading')
          .eq('car_id', carId)
          // Latest = highest reading value (regardless of date); if equal, newest date wins
          .order('reading', { ascending: false })
          .order('recorded_at', { ascending: false })
          .limit(10); // Fetch top 10 to check for outliers
        
        if (error) {
          console.error('Error fetching latest odometer reading:', error);
          return;
        }
        
        if (!data || data.length === 0) {
          // Fallback to car.mileage if no odometer readings exist
          setLatestOdometerReading(currentCar?.mileage || null);
          return;
        }
        
        // Filter out obvious outliers
        const { filterOdometerOutliers } = await import('@/lib/utils');
        const validReading = filterOdometerOutliers(data);
        setLatestOdometerReading(validReading);
      } catch (err) {
        console.error('Error fetching latest odometer reading:', err);
        // Fallback to car.mileage on error
        setLatestOdometerReading(currentCar?.mileage || null);
      }
    };
    
    fetchLatestOdometer();
  }, [carId, currentCar?.mileage]);

  // Normalize date to YYYY-MM-DD for <input type="date"> (DB may return ISO timestamp)
  const toDateOnly = (value: string | null | undefined): string => {
    if (!value) return '';
    const s = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (currentCar) {
      // Use latest odometer reading if available, otherwise fallback to car.mileage
      const displayMileage = latestOdometerReading ?? currentCar.mileage;
      // Convert to user's distance unit if needed
      const mileageForDisplay = displayMileage && settings?.distanceUnit === 'mi' 
        ? Math.round(displayMileage / 1.609344) 
        : displayMileage;
      
      form.reset({
        brand: currentCar.brand,
        model: currentCar.model,
        year: currentCar.year?.toString() || '',
        nickname: currentCar.nickname || '',
        licensePlate: currentCar.licensePlate || '',
        vin: currentCar.vin || '',
        mileage: mileageForDisplay ? mileageForDisplay.toString() : '',
        transmission: currentCar.transmission,
        fuelType: currentCar.fuelType,
        // Wipers
        wiperDriversSideSize: (currentCar as any).wiper_drivers_side_size || (currentCar as any).wiperDriversSideSize || '',
        wiperPassangerSideSize: (currentCar as any).wiper_passanger_side_size || (currentCar as any).wiperPassangerSideSize || '',
        wiperRearSize: (currentCar as any).wiper_rear_size || (currentCar as any).wiperRearSize || '',
        wiperLastReplacement: (currentCar as any).wiper_last_replacement || (currentCar as any).wiperLastReplacement || '',
        lastServiceDate: toDateOnly(currentCar.lastServiceDate),
        winterTiresBrand: currentCar.winterTiresBrand || '',
        winterTiresSize: currentCar.winterTiresSize || '',
        summerTiresBrand: currentCar.summerTiresBrand || '',
        summerTiresSize: currentCar.summerTiresSize || '',
        tirePressure: (currentCar as any).tirePressure || '',
        octaPolicyNumber: currentCar.octaPolicyNumber || '',
        octaExpiry: toDateOnly(currentCar.octaExpiry),
        octaProvider: currentCar.octaProvider || '',
        octaClaimsHotline: (currentCar as any).octaClaimsHotline || '',
        kaskoPolicyNumber: currentCar.kaskoPolicyNumber || '',
        kaskoExpiry: toDateOnly(currentCar.kaskoExpiry),
        kaskoProvider: currentCar.kaskoProvider || '',
        kaskoClaimsHotline: (currentCar as any).kaskoClaimsHotline || '',
      });
    }
  }, [currentCar, form, latestOdometerReading, settings?.distanceUnit]);

  useEffect(() => {
    if (currentCar?.image_path) {
      setImageLoading(true);
      getSignedViewUrl(currentCar.image_path)
        .then(signedUrlInfo => {
          setImageUrl(signedUrlInfo.url);
          // Keep loading state true until image is fully loaded
          // The image will trigger onLoad/onError to set loading to false
        })
        .catch((e) => {
          console.error('Failed to generate signed URL for image:', e);
          setImageLoading(false);
        });
    }
  }, [currentCar?.image_path]);

  // Fail-safe timeout to prevent perpetual spinners
  useEffect(() => {
    if (imageLoading && imageUrl) {
      const TIMEOUT_MS = 5000;
      const timeout = setTimeout(() => {
        console.warn(`⏰ Timeout hit for EditCarPage image, forcing loading state to false`);
        setImageLoading(false);
      }, TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }
  }, [imageLoading, imageUrl]);

  if (loading) {
    return <div className="flex justify-center items-center h-full"><LoadingSpinner /></div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!currentCar) {
    return (
      <PageLayout 
        title={t('cars.editTitle') || 'Edit car'} 
        showBackButton 
        onBack={() => navigate('/cars')}
      >
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">{t('cars.edit.notFound') || 'Car not found'}</p>
          <Button onClick={() => navigate('/cars')}>
            {t('cars.edit.backToCars') || 'Back to My Cars'}
          </Button>
        </div>
      </PageLayout>
    );
  }
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) {
      toast({
        title: t('cars.edit.errorTitle') || 'Update failed',
        description: t('common.pleaseSignIn') || 'Please sign in to update the car.',
        variant: 'destructive',
      });
      return;
    }
    try {
      let imagePath = currentCar.image_path;

      // Only need session when uploading a new image
      if (selectedFile) {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          toast({
            title: t('cars.edit.errorTitle') || 'Update failed',
            description: t('common.pleaseSignIn') || 'Please sign in again to upload an image.',
            variant: 'destructive',
          });
          return;
        }
        setImageUploading(true);
        try {
          imagePath = await uploadCarImage({
            file: selectedFile,
            userId: user.id,
            entityId: carId!,
            type: 'car',
            accessToken: currentSession.access_token,
          });
        } catch (uploadError) {
          console.error('uploadCarImage error:', uploadError);
          throw uploadError;
        }
      }

      // If mileage provided, write an odometer log entry instead of updating cars.mileage
      if (values.mileage) {
        const reading = parseInt(values.mileage, 10);
        if (!Number.isNaN(reading)) {
          const readingKm = settings?.distanceUnit === 'mi' ? Math.round(reading * 1.609344) : reading;
          try {
            await supabase.from('odometer_readings').insert({
              car_id: carId,
              entered_by: user.id,
              reading: readingKm,
              source: 'manual',
            });
          } catch (e) {
            console.warn('Failed to insert odometer reading:', e);
          }
        }
      }

      // Upload OCTA / KASKO pending documents if any
      let uploadedOctaPaths: string[] = [];
      let uploadedKaskoPaths: string[] = [];
      if (octaPendingFiles.length > 0 || kaskoPendingFiles.length > 0) {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession?.access_token) {
          toast({
            title: t('cars.edit.errorTitle') || 'Update failed',
            description: t('common.pleaseSignIn') || 'Please sign in again to upload documents.',
            variant: 'destructive',
          });
          return;
        }
        setImageUploading(true);
        try {
          for (const file of octaPendingFiles) {
            const path = await uploadCarImage({
              file,
              userId: user.id,
              entityId: carId!,
              type: 'octa',
              accessToken: currentSession.access_token,
            });
            uploadedOctaPaths.push(path);
          }
          for (const file of kaskoPendingFiles) {
            const path = await uploadCarImage({
              file,
              userId: user.id,
              entityId: carId!,
              type: 'kasko',
              accessToken: currentSession.access_token,
            });
            uploadedKaskoPaths.push(path);
          }
        } catch (docErr) {
          console.error('Document upload error:', docErr);
          toast({
            title: t('cars.edit.errorTitle') || 'Upload failed',
            description: (docErr instanceof Error ? docErr.message : 'Failed to upload documents'),
            variant: 'destructive',
          });
          setImageUploading(false);
          return;
        }
        setImageUploading(false);
      }
      const finalOcta = [...octaMediaPaths, ...uploadedOctaPaths].slice(0, 5);
      const finalKasko = [...kaskoMediaPaths, ...uploadedKaskoPaths].slice(0, 5);

      await updateCar(carId!, ({
        brand: values.brand,
        model: values.model,
        year: parseInt(values.year),
        nickname: values.nickname || undefined,
        licensePlate: values.licensePlate || undefined,
        vin: values.vin || undefined,
        // mileage now stored via odometer_readings
        transmission: values.transmission,
        fuelType: values.fuelType,
        // Wipers to snake_case
        wiperDriversSideSize: (values as any).wiperDriversSideSize || undefined,
        wiperPassangerSideSize: (values as any).wiperPassangerSideSize || undefined,
        wiperRearSize: (values as any).wiperRearSize || undefined,
        wiperLastReplacement: (values as any).wiperLastReplacement || undefined,
        lastServiceDate: values.lastServiceDate || undefined,
        tirePressure: values.tirePressure || undefined,
        winterTiresBrand: values.winterTiresBrand || undefined,
        winterTiresSize: values.winterTiresSize || undefined,
        summerTiresBrand: values.summerTiresBrand || undefined,
        summerTiresSize: values.summerTiresSize || undefined,
        octaPolicyNumber: values.octaPolicyNumber || undefined,
        octaExpiry: values.octaExpiry || undefined,
        octaProvider: values.octaProvider || undefined,
        octaClaimsHotline: (values as any).octaClaimsHotline || undefined,
        octaMediaPaths: finalOcta,
        kaskoPolicyNumber: values.kaskoPolicyNumber || undefined,
        kaskoExpiry: values.kaskoExpiry || undefined,
        kaskoProvider: values.kaskoProvider || undefined,
        kaskoClaimsHotline: (values as any).kaskoClaimsHotline || undefined,
        kaskoMediaPaths: finalKasko,
        image_path: imagePath, // 🆕 update image path only if needed
      } as any));
      
      toast({
        title: t('cars.edit.successTitle') || 'Car updated',
        description: t('cars.edit.successDesc') || 'Your car details have been updated successfully',
      });

      setSelectedFile(null); // reset
      setLocalPreview(null); // reset
      setOctaPendingFiles([]);
      setKaskoPendingFiles([]);
      navigate(`/cars/${carId}`);
    } catch (error) {
      console.error('Error updating car:', error);
      toast({
        title: t('cars.edit.errorTitle') || 'Update failed',
        description: error instanceof Error ? error.message : t('cars.edit.errorDesc') || 'Failed to update car details',
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddImageClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview(previewUrl);
    setSelectedFile(file); // 🆕 store for later upload
    setImageLoading(true); // Show loading spinner for new image
  };

  const handleRemoveImage = async () => {
    setImageUrl('');
    setImageLoading(false); // Reset loading state
    if (localPreview) {
      URL.revokeObjectURL(localPreview);
      setLocalPreview(null);
    }
    try {
      await updateCar(carId!, { image_url: undefined });
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: t('cars.edit.removeFailedTitle') || 'Remove failed',
        description: error instanceof Error ? error.message : t('cars.edit.removeFailedDesc') || 'Failed to remove image',
        variant: "destructive",
      });
    }
  };

  const toggleAccordion = (key: 'basic' | 'tires' | 'wipers' | 'octa' | 'kasko') => {
    setActiveAccordion(prev => (prev === key ? null : key));
  };

  const onInvalid = (errors: Record<string, { message?: string }>) => {
    console.warn('Edit car validation failed:', errors);
    const firstError = Object.values(errors)[0]?.message;
    toast({
      title: t('cars.edit.errorTitle') || 'Update failed',
      description: firstError || (t('cars.edit.fixErrors') || 'Please fix the errors below and try again.'),
      variant: 'destructive',
    });
  };

  // Dirty indicators per accordion
  const dirtyFields = (form.formState?.dirtyFields || {}) as Record<string, any>;
  const basicDirty = !!(dirtyFields.nickname || dirtyFields.brand || dirtyFields.model || dirtyFields.year || dirtyFields.licensePlate || dirtyFields.vin || dirtyFields.mileage || dirtyFields.transmission || dirtyFields.fuelType);
  const tiresDirty = !!(dirtyFields.winterTiresBrand || dirtyFields.winterTiresSize || dirtyFields.summerTiresBrand || dirtyFields.summerTiresSize || dirtyFields.tirePressure);
  const wipersDirty = !!(dirtyFields.wiperDriversSideSize || dirtyFields.wiperPassangerSideSize || dirtyFields.wiperRearSize || dirtyFields.wiperLastReplacement);
  const octaDirty = !!(dirtyFields.octaProvider || dirtyFields.octaPolicyNumber || dirtyFields.octaClaimsHotline || dirtyFields.octaExpiry);
  const kaskoDirty = !!(dirtyFields.kaskoProvider || dirtyFields.kaskoPolicyNumber || dirtyFields.kaskoClaimsHotline || dirtyFields.kaskoExpiry);
  
  return (
    <PageLayout 
      title={t('cars.editTitle') || 'Edit car'} 
      showBackButton 
      onBack={() => navigate(-1)}
    >
      <div className="max-w-md mx-auto px-4 pb-24">
        <div className="flex flex-col items-center mb-4 mt-6">
          <div
            className="relative flex items-center justify-center w-full"
            style={{ aspectRatio: '2 / 1' }}
          >
          {imageLoading ? (
            <div className="flex flex-col items-center justify-center">
              <LoadingSpinner />
              <p className="text-sm text-gray-500 mt-2">
                {t('cars.edit.loadingImage') || 'Loading image...'}
              </p>
            </div>
          ) : (
            <>
              {(localPreview || imageUrl) ? (
                <>
                  <img
                    src={localPreview || imageUrl}
                    alt={t('car.nickname') || 'Car'}
                    className="object-cover w-full h-full rounded-2xl shadow-md opacity-100 transition-opacity duration-200"
                    onLoad={() => setImageLoading(false)}
                    onError={() => setImageLoading(false)}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-1 right-1 bg-black bg-opacity-60 rounded-full p-1 text-white"
                    title={t('cars.edit.removeImageTitle') || 'Remove image'}
                  >
                    <X size={18} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleAddImageClick}
                  className="w-full h-full flex flex-col items-center justify-center border-2 border-gray-300 rounded-xl bg-gray-50 shadow-[0px_10px_8px_-1px_#EEEEEE] hover:border-lime transition-colors"
                  style={{ aspectRatio: '2 / 1' }}
                  disabled={imageUploading}
                >
                  <img
                    src="/icons/AddCarImage.png"
                    alt={t('cars.edit.addImageAlt') || 'Add'}
                    className="w-12 h-12 opacity-60"
                    style={{ filter: 'invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0) contrast(100%)' }}
                  />
                </button>
              )}
            </>
          )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageInputChange}
              accept="image/*"
              className="hidden"
            />
          </div>
          <div className="h-4" />
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            className="space-y-4"
          >
            <div className="w-full">
            {/* Basic information accordion */}
            <div
              className="w-full"
              style={{ borderBottom: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            >
              <button
                type="button"
                className="flex flex-1 items-center justify-between w-full py-4 font-medium"
                onClick={() => toggleAccordion('basic')}
              >
                <span className="flex-1 text-left">
                  <span className="inline-flex items-center gap-2">
                    <span>{t('cars.edit.basicInfoSection') || 'Basic information'}</span>
                    {basicDirty && <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#e7fe52' }} />}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ transform: activeAccordion === 'basic' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              {activeAccordion === 'basic' && (
                <div className="mt-2 mb-3 space-y-3">
            {/* Basic car info */}
            <div className="rounded-2xl bg-white shadow-md p-4 space-y-3">
          <FormField
            control={form.control}
            name="nickname"
            render={({ field }) => (
            <FormItem className="w-full max-w-full flex flex-col">
              <FormLabel
                className="block text-sm text-gray-600 mb-1"
              >
                  {t('cars.edit.nicknameLabel') || 'Nickname (optional)'}
              </FormLabel>
                <FormControl>
                <Input
                  {...field}
                  className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                  placeholder={t('cars.edit.nicknamePlaceholder') || "Mom's car"}
                />
                </FormControl>
              </FormItem>
            )}
          />
          
            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
            <FormItem className="w-full max-w-full flex flex-col">
              <FormLabel
                className="block text-sm text-gray-600 mb-1"
              >
                  {t('cars.edit.brandLabel') || 'Make *'}
              </FormLabel>
                  <FormControl>
                <Input
                  {...field}
                  className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                  placeholder={t('cars.edit.brandPlaceholder') || 'Toyota'}
                />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
            <FormItem className="w-full max-w-full flex flex-col">
              <FormLabel
                className="block text-sm text-gray-600 mb-1"
              >
                  {t('cars.edit.modelLabel') || 'Model *'}
              </FormLabel>
                  <FormControl>
                  <Input
                    {...field}
                    className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                    placeholder={t('cars.edit.modelPlaceholder') || 'Corolla'}
                  />
                  </FormControl>
                </FormItem>
              )}
            />
          
            <FormField
              control={form.control}
              name="year"
              render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
                <FormLabel
                  className="block text-sm text-gray-600 mb-1"
                >
                  {t('cars.edit.yearLabel') || 'Year'} *
                </FormLabel>
                  <FormControl>
                  <Input
                    {...field}
                    className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                    placeholder={t('cars.edit.yearPlaceholder') || '2020'}
                  />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="licensePlate"
              render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
<FormLabel className="block text-sm text-gray-600 mb-2">
                  {t('cars.edit.licensePlateLabel') || 'License plate'} *
                </FormLabel>
                  <FormControl>
                  <Input
                    {...field}
                    className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                    placeholder={t('cars.edit.licensePlatePlaceholder') || 'ABC123'}
                  />
                  </FormControl>
                </FormItem>
              )}
            />
          
          <FormField
            control={form.control}
            name="vin"
            render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
<FormLabel className="block text-sm text-gray-600 mb-2">
                  {t('car.vinLabel') || 'VIN (optional)'}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                    placeholder={t('cars.edit.vinPlaceholder') || '1HGCM82633A123456'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          </div>

          {/* Mileage + Transmission + Fuel */}
          <div className="rounded-2xl bg-white shadow-md p-4 space-y-3">
          
          <FormField
            control={form.control}
            name="mileage"
            render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
                <FormLabel
                  className="block text-sm text-gray-600 mb-1"
                >
                  {t('cars.edit.mileageLabel') || 'Mileage (optional)'}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    className="w-full max-w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                    placeholder={t('cars.edit.mileagePlaceholder') || '45000'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Mileage date removed */}

            <FormField
              control={form.control}
              name="transmission"
              render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
<FormLabel className="block text-sm text-gray-600 mb-2">
                  {t('cars.edit.transmissionLabel') || 'Transmission'} *
                </FormLabel>
                  <Select 
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                    <SelectTrigger 
                      className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm"
                    >
                      <SelectValue 
                        placeholder={t('cars.edit.transmissionPlaceholder') || 'Select transmission'} 
                        className="custom-select-placeholder"
                        style={{ color: '#B5B5B5' }}
                      />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="manual">
                        {t('car.transmission.manual') || 'Manual'}
                      </SelectItem>
                      <SelectItem value="automatic">
                        {t('car.transmission.automatic') || 'Automatic'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="fuelType"
              render={({ field }) => (
              <FormItem className="w-full max-w-full flex flex-col">
<FormLabel className="block text-sm text-gray-600 mb-2">
                  {t('cars.edit.fuelTypeLabel') || 'Fuel type'} *
                </FormLabel>
                  <Select 
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                    <SelectTrigger 
                      className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm"
                    >
                      <SelectValue 
                        placeholder={t('cars.edit.fuelTypePlaceholder') || 'Select fuel type'} 
                        className="custom-select-placeholder"
                        style={{ color: '#B5B5B5' }}
                      />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="gasoline">
                        {t('car.fuelType.gasoline') || 'Gasoline'}
                      </SelectItem>
                      <SelectItem value="diesel">
                        {t('car.fuelType.diesel') || 'Diesel'}
                      </SelectItem>
                      <SelectItem value="electric">
                        {t('car.fuelType.electric') || 'Electric'}
                      </SelectItem>
                      <SelectItem value="hybrid">
                        {t('car.fuelType.hybrid') || 'Hybrid'}
                      </SelectItem>
                      <SelectItem value="lpg">
                        {t('car.fuelType.lpg') || 'LPG'}
                      </SelectItem>
                      <SelectItem value="cng">
                        {t('car.fuelType.cng') || 'CNG'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
                </div>
              )}
            </div>

          {/* Accordion Sections (match Car Details accordion rhythm) */}
          <div className="w-full">
            {/* Tires Accordion */}
            <div ref={tiresRef}
              style={{
                width: '100%',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 0,
                boxSizing: 'border-box',
              }}
            >
              <button
                type="button"
                className="flex flex-1 items-center justify-between w-full py-4 font-medium"
                onClick={() => toggleAccordion('tires')}
              >
                <span className="flex-1 text-left">
                  <span className="inline-flex items-center gap-2">
                    <span>{t('cars.edit.tiresSection') || 'Tires'}</span>
                    {tiresDirty && <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#e7fe52' }} />}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ transform: activeAccordion === 'tires' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              {activeAccordion === 'tires' && (
                <div className="mt-2 mb-3 rounded-2xl bg-white border border-gray-200 shadow-md p-4 space-y-4">
                  <FormField control={form.control} name="winterTiresBrand" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.winterBrandLabel') || 'Winter | Brand (optional)'}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.tireBrandPlaceholder') || 'Michelin'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="winterTiresSize" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.winterSizeLabel') || 'Winter | Size (optional)'}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          value={formatTireSizeInput(field.value)}
                          onChange={(e) => {
                            const masked = formatTireSizeInput(e.target.value);
                            field.onChange(masked);
                            const target = e.target as HTMLInputElement;
                            const pos = computeCaretPos(masked);
                            setTimeout(() => target.setSelectionRange(pos, pos), 0);
                          }}
                          inputMode="numeric"
                          maxLength={12}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.tireSizePlaceholder') || '___ / __ R__'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="summerTiresBrand" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.summerBrandLabel') || 'Summer | Brand (optional)'}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.tireBrandPlaceholder') || 'Michelin'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="summerTiresSize" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.summerSizeLabel') || 'Summer | Size (optional)'}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          value={formatTireSizeInput(field.value)}
                          onChange={(e) => {
                            const masked = formatTireSizeInput(e.target.value);
                            field.onChange(masked);
                            const target = e.target as HTMLInputElement;
                            const pos = computeCaretPos(masked);
                            setTimeout(() => target.setSelectionRange(pos, pos), 0);
                          }}
                          inputMode="numeric"
                          maxLength={12}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.tireSizePlaceholder') || '___ / __ R__'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="tirePressure" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.tirePressureLabel') || 'Tire pressure (optional)'}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.tirePressurePlaceholder') || 'e.g. 2.3 bar'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />

                </div>
              )}
            </div>
            {/* Wiper blades Accordion */}
            <div ref={wipersRef}
              style={{
                width: '100%',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 0,
                boxSizing: 'border-box',
              }}
            >
              <button
                type="button"
                className="flex flex-1 items-center justify-between w-full py-4 font-medium"
                onClick={() => toggleAccordion('wipers')}
              >
                <span className="flex-1 text-left">
                  <span className="inline-flex items-center gap-2">
                    <span>{t('cars.edit.wipersSection') || 'Wiper blades'}</span>
                    {wipersDirty && <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#e7fe52' }} />}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ transform: activeAccordion === 'wipers' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              {activeAccordion === 'wipers' && (
                <div className="mt-2 mb-3 rounded-2xl bg-white border border-gray-200 shadow-md p-4 space-y-4">
                  <FormField control={form.control} name="wiperDriversSideSize" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.wipersDriverLabel') || "Driver's side size"} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.wipersDriverPlaceholder') || 'e.g. 26'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="wiperPassangerSideSize" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.wipersPassengerLabel') || 'Passenger side size'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.wipersPassengerPlaceholder') || 'e.g. 18'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="wiperRearSize" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.wipersRearLabel') || 'Rear size'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.wipersRearPlaceholder') || 'e.g. 12'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="wiperLastReplacement" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.wipersLastReplacementLabel') || 'Last replacement'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.datePlaceholder') || 'YYYY-MM-DD'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              )}
            </div>
            {/* OCTA Accordion */}
            <div ref={octaRef}
              style={{
                width: '100%',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 0,
                boxSizing: 'border-box',
              }}
            >
              <button
                type="button"
                className="flex flex-1 items-center justify-between w-full py-4 font-medium"
                onClick={() => toggleAccordion('octa')}
              >
                <span className="flex-1 text-left">
                  <span className="inline-flex items-center gap-2">
                    <span>{t('cars.edit.octaSection') || 'OCTA'}</span>
                    {octaDirty && <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#e7fe52' }} />}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ transform: activeAccordion === 'octa' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              {activeAccordion === 'octa' && (
                <div className="mt-2 mb-3 rounded-2xl bg-white border border-gray-200 shadow-md p-4 space-y-4">
                  <FormField control={form.control} name="octaProvider" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.octaProviderLabel') || 'Provider'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.octaProviderPlaceholder') || 'If P&C'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="octaPolicyNumber" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.octaPolicyLabel') || 'Policy number'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400" 
                          placeholder={t('cars.edit.octaPolicyPlaceholder') || 'OCTA123456'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="octaClaimsHotline" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.octaClaimsLabel') || 'Claims hotline'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.claimsPhonePlaceholder') || '+371 6XXXXXXX'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="octaExpiry" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.dateLabel') || 'Expiry date'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.datePlaceholder') || 'YYYY-MM-DD'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  {/* OCTA documents (up to 5: images or PDF) */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">{t('cars.edit.documents') || 'Documents'} {t('common.optionalField')}</p>
                    <div className="flex flex-wrap gap-2">
                      {octaMediaPaths.map((path, i) => (
                        <span key={path} className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs">
                          {path.toLowerCase().endsWith('.pdf') ? <FileText className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          <span className="truncate max-w-[120px]">Doc {i + 1}</span>
                          <button type="button" onClick={() => setOctaMediaPaths(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-600" aria-label="Remove"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      {octaPendingFiles.map((file, i) => (
                        <span key={`${file.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs">
                          {file.type === 'application/pdf' ? <FileText className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          <span className="truncate max-w-[120px]">{file.name}</span>
                          <button type="button" onClick={() => setOctaPendingFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-600" aria-label="Remove"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                    {octaMediaPaths.length + octaPendingFiles.length < 5 && (
                      <>
                        <input ref={octaFileInputRef} type="file" accept="image/*,.pdf" className="hidden" multiple onChange={(e) => {
                          const files = e.target.files ? Array.from(e.target.files) : [];
                          const remaining = 5 - octaMediaPaths.length - octaPendingFiles.length;
                          setOctaPendingFiles(prev => [...prev, ...files.slice(0, remaining)].slice(0, 5 - octaMediaPaths.length));
                          e.target.value = '';
                        }} />
                        <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => octaFileInputRef.current?.click()}>
                          {t('cars.edit.addDocument') || 'Add document'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* KASKO Accordion */}
            <div ref={kaskoRef}
              style={{
                width: '100%',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 0,
                boxSizing: 'border-box',
              }}
            >
              <button
                type="button"
                className="flex flex-1 items-center justify-between w-full py-4 font-medium"
                onClick={() => toggleAccordion('kasko')}
              >
                <span className="flex-1 text-left">
                  <span className="inline-flex items-center gap-2">
                    <span>{t('cars.edit.kaskoSection') || 'KASKO'}</span>
                    {kaskoDirty && <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#e7fe52' }} />}
                  </span>
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ transform: activeAccordion === 'kasko' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>
              {activeAccordion === 'kasko' && (
                <div className="mt-2 mb-3 rounded-2xl bg-white border border-gray-200 shadow-md p-4 space-y-4">
                  <FormField control={form.control} name="kaskoProvider" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.kaskoProviderLabel') || t('cars.edit.octaProviderLabel') || 'Provider'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.kaskoProviderPlaceholder') || 'If P&C'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="kaskoPolicyNumber" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.kaskoPolicyLabel') || t('cars.edit.octaPolicyLabel') || 'Policy number'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.kaskoPolicyPlaceholder') || 'KASKO123456'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="kaskoClaimsHotline" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.kaskoClaimsLabel') || 'Claims hotline'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.claimsPhonePlaceholder') || '+371 6XXXXXXX'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="kaskoExpiry" render={({ field }) => (
                    <FormItem>
<FormLabel className="block text-sm text-gray-600 mb-2">
                        {t('cars.edit.dateLabel') || 'Expiry date'} {t('common.optionalField')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field} 
                          className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm placeholder:text-gray-400"
                          placeholder={t('cars.edit.datePlaceholder') || 'YYYY-MM-DD'}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  {/* KASKO documents (up to 5: images or PDF) */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">{t('cars.edit.documents') || 'Documents'} {t('common.optionalField')}</p>
                    <div className="flex flex-wrap gap-2">
                      {kaskoMediaPaths.map((path, i) => (
                        <span key={path} className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1 text-xs">
                          {path.toLowerCase().endsWith('.pdf') ? <FileText className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          <span className="truncate max-w-[120px]">Doc {i + 1}</span>
                          <button type="button" onClick={() => setKaskoMediaPaths(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-600" aria-label="Remove"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      {kaskoPendingFiles.map((file, i) => (
                        <span key={`${file.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs">
                          {file.type === 'application/pdf' ? <FileText className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          <span className="truncate max-w-[120px]">{file.name}</span>
                          <button type="button" onClick={() => setKaskoPendingFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-600" aria-label="Remove"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                    {kaskoMediaPaths.length + kaskoPendingFiles.length < 5 && (
                      <>
                        <input ref={kaskoFileInputRef} type="file" accept="image/*,.pdf" className="hidden" multiple onChange={(e) => {
                          const files = e.target.files ? Array.from(e.target.files) : [];
                          const remaining = 5 - kaskoMediaPaths.length - kaskoPendingFiles.length;
                          setKaskoPendingFiles(prev => [...prev, ...files.slice(0, remaining)].slice(0, 5 - kaskoMediaPaths.length));
                          e.target.value = '';
                        }} />
                        <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => kaskoFileInputRef.current?.click()}>
                          {t('cars.edit.addDocument') || 'Add document'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
            </div>
          
          <div className="pt-4 pb-12 relative z-10">
            <Button
              type="button"
              className="w-full rounded-xl bg-[#e7fe52] text-black text-sm font-medium hover:bg-[#e7fe52]/90"
              onClick={() => form.handleSubmit(onSubmit, onInvalid)()}
              disabled={imageUploading}
            >
              {imageUploading ? (t('common.saving') || 'Saving...') : (t('cars.edit.updateButton') || 'Update car')}
            </Button>
          </div>
          </form>
        </Form>
      </div>
    </PageLayout>
  );
};

export default EditCarPage;
